import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ArrowForwardRounded,
  CheckCircleRounded,
  RemoveCircleOutlineRounded,
} from "@mui/icons-material";
import {
  pricingCapabilityCatalog,
  pricingPlans,
  trialConfig,
} from "../config/pricingConfig";
import WaitlistSignupForm from "../components/WaitlistSignupForm";

const planContent = {
  basic: {
    name: "Free",
    description: "Build a consistent interview practice habit.",
    monthly: "Free",
    annual: "Free",
    billingNote: "per month for Launchpad access",
    features: ["Limited practice interviews", "Basic scoring", "Basic interview tracks"],
  },
  pro: {
    name: "Pro",
    description: "For focused preparation across every stage.",
    monthly: "₹1,499",
    annual: "₹1,199",
    billingNote: "per month",
    annualNote: "₹14,388 billed annually",
    features: [
      "Unlimited interviews",
      "Detailed reports",
      "Full interview track library",
      "Personalized roadmap",
      "Session history",
    ],
  },
  enterprise: {
    name: "Career Plus",
    description: "Deeper preparation with expert support.",
    monthly: "₹7,999",
    annual: "₹6,399",
    billingNote: "per month",
    annualNote: "₹76,788 billed annually",
    features: [
      "Everything in Pro",
      "Expert-reviewed feedback",
      "Advanced role preparation",
      "Priority support",
    ],
  },
};

export default function PricingPage() {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");

  const handleBillingChange = (_event, value) => {
    if (value) setBillingCycle(value);
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "#fff", color: "#0b1020", py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 4, lg: 6 } }}>
        <Box component="header" sx={{ textAlign: "center", maxWidth: 760, mx: "auto", mb: { xs: 4, md: 6 } }}>
          <Chip
            label={trialConfig.label}
            size="small"
            sx={{ mb: 2, bgcolor: "#f3f5ff", color: "#3e5af5", border: "1px solid #dbe1ff" }}
          />
          <Typography component="h1" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, lineHeight: 1.1 }}>
            Simple plans for serious practice
          </Typography>
          <Typography sx={{ mt: 2, color: "#6b7280", fontSize: { xs: 16, md: 18 }, lineHeight: 1.65 }}>
            Start free, then choose the level of feedback and support that fits your interview goals.
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={billingCycle}
            onChange={handleBillingChange}
            aria-label="Billing cycle"
            sx={{
              mt: 3,
              p: 0.5,
              bgcolor: "#f3f4f6",
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                minHeight: 44,
                px: 2.5,
                py: 0.75,
                border: 0,
                borderRadius: "9px !important",
                color: "#6b7280",
                textTransform: "none",
                fontWeight: 700,
                "&.Mui-selected": { bgcolor: "#fff", color: "#3048d8", boxShadow: "0 2px 8px rgba(15,23,42,.08)" },
                "&:focus-visible": { outline: "3px solid #cbd5ff", outlineOffset: 2 },
              },
            }}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="annual">Annual</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: "#6b7280" }}>
            Annual plans are billed once per year.
          </Typography>
        </Box>

        <Grid container spacing={2.5} alignItems="stretch">
          {pricingPlans.map((plan) => {
            const content = planContent[plan.key];
            const highlighted = plan.key === "pro";
            const price = billingCycle === "annual" ? content.annual : content.monthly;
            return (
              <Grid item xs={12} md={4} key={plan.key}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: highlighted ? "#4f6bff" : "#e8ecf4",
                    boxShadow: highlighted ? "0 20px 48px rgba(79,107,255,.12)" : "none",
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  {highlighted && (
                    <Chip
                      label="Most popular"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        bgcolor: "#4f6bff",
                        color: "#fff",
                      }}
                    />
                  )}
                  <CardContent sx={{ p: { xs: 3, md: 3.5 }, width: "100%", display: "flex", flexDirection: "column" }}>
                    <Typography component="h2" sx={{ fontWeight: 800, fontSize: 22, pr: highlighted ? 12 : 0 }}>
                      {content.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 1, minHeight: { md: 44 } }}>
                      {content.description}
                    </Typography>

                    <Box sx={{ mt: 3, minHeight: 82 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 38, lineHeight: 1.1 }}>
                        {price}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.75 }}>
                        {plan.key === "basic" ? (
                          <>Then <Box component="span">₹499</Box> {content.billingNote}</>
                        ) : billingCycle === "annual" && content.annualNote ? content.annualNote : content.billingNote}
                      </Typography>
                    </Box>

                    <Button
                      variant={highlighted ? "contained" : "outlined"}
                      endIcon={<ArrowForwardRounded />}
                      onClick={() => navigate(`/checkout/${plan.key}`, { state: { billingCycle } })}
                      sx={{
                        mt: 2,
                        minHeight: 48,
                        borderRadius: "12px",
                        bgcolor: highlighted ? "#4f6bff" : "#fff",
                        borderColor: highlighted ? "#4f6bff" : "#d1d5db",
                        color: highlighted ? "#fff" : "#111827",
                        "&:hover": { bgcolor: highlighted ? "#3e5af5" : "#f8fafc" },
                      }}
                    >
                      {plan.ctaLabel}
                    </Button>

                    <Typography variant="subtitle2" sx={{ fontWeight: 750, color: "#111827", mt: 3, mb: 1 }}>
                      {content.name === "Free" ? "Free includes:" : `${content.name} includes:`}
                    </Typography>
                    <List disablePadding sx={{ flex: 1 }}>
                      {content.features.map((feature) => (
                        <ListItem key={feature} disableGutters sx={{ py: 0.7, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: 30, mt: 0.15 }}>
                            <CheckCircleRounded aria-hidden="true" sx={{ fontSize: 19, color: "#21c36f" }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} primaryTypographyProps={{ variant: "body2", color: "#374151" }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box component="section" aria-labelledby="plan-comparison-title" sx={{ mt: { xs: 6, md: 8 } }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography id="plan-comparison-title" component="h2" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 32 } }}>
              Compare plan access
            </Typography>
            <Typography sx={{ color: "#6b7280", mt: 1 }}>Included in every plan where marked.</Typography>
          </Box>
          <Grid container spacing={2}>
            {pricingPlans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.key}>
                <Card elevation={0} sx={{ height: "100%", borderRadius: "12px", border: "1px solid #e8ecf4", boxShadow: "none" }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography component="h3" sx={{ fontWeight: 750, mb: 1 }}>{planContent[plan.key].name}</Typography>
                    <List dense disablePadding>
                      {pricingCapabilityCatalog.map((capability) => {
                        const access = plan.capabilityAccess[capability.key];
                        const included = Boolean(access?.included);
                        return (
                          <ListItem key={capability.key} disableGutters sx={{ py: 0.6, alignItems: "flex-start" }}>
                            <ListItemIcon sx={{ minWidth: 30, mt: 0.1 }}>
                              {included ? (
                                <CheckCircleRounded
                                  aria-label={`${capability.label} included`}
                                  sx={{ fontSize: 18, color: "#21c36f" }}
                                />
                              ) : (
                                <RemoveCircleOutlineRounded
                                  aria-label={`${capability.label} not included`}
                                  sx={{ fontSize: 18, color: "#9ca3af" }}
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={capability.label}
                              secondary={access?.value}
                              primaryTypographyProps={{ variant: "body2", color: included ? "#374151" : "#9ca3af" }}
                              secondaryTypographyProps={{ variant: "caption", color: included ? "#6b7280" : "#9ca3af" }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Stack
          component="section"
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mt: 4, p: { xs: 2.5, sm: 3 }, bgcolor: "#f7f8ff", border: "1px solid #e2e5ff", borderRadius: "12px" }}
        >
          <Box>
            <Typography component="h2" sx={{ fontWeight: 750, fontSize: 18 }}>Not sure which plan fits?</Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>Start free and move to Pro when you need unlimited practice.</Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate("/checkout/pro")} sx={{ bgcolor: "#fff", color: "#3e5af5" }}>
            Compare from Career Sprint
          </Button>
        </Stack>

        <Box sx={{ mt: 4 }}>
          <WaitlistSignupForm
            sourcePage="pricing"
            intent="free_trial"
            compact
            title="Join the free-trial waitlist"
            helperText="Leave your email to get notified when new 5-minute trial slots open."
          />
        </Box>
      </Container>
    </Box>
  );
}
